import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import toast from 'react-hot-toast'
import { api } from '../services/api'
import { useIdleTimeout } from './useIdleTimeout'

// 2 hours of inactivity → auto-save any open timesheet, then log out.
const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)

    // Pages that have unsaved work (TimesheetEntry) register a save callback here
    // so we can persist drafts before the idle logout happens.
    const idleSaversRef = useRef(new Set())

    useEffect(() => {
        checkAuth()
    }, [])

    async function checkAuth() {
        try {
            // Surface any message left over from a forced logout (idle timeout or 401).
            const authMessage = sessionStorage.getItem('jt_auth_message')
            if (authMessage) {
                sessionStorage.removeItem('jt_auth_message')
                // Defer slightly so the Toaster is mounted before we fire the toast.
                setTimeout(() => toast.error(authMessage, { duration: 6000 }), 100)
            }

            const storedUser = localStorage.getItem('jt_user')
            if (storedUser) {
                const userData = JSON.parse(storedUser)
                setUser(userData)
                // Refresh user data from server to pick up permission changes
                try {
                    const response = await api.get('/api/auth/me')
                    if (response.success && response.user) {
                        const refreshed = {
                            ...response.user,
                            token: userData.token
                        }
                        setUser(refreshed)
                        localStorage.setItem('jt_user', JSON.stringify(refreshed))
                    }
                } catch (err) {
                    // If refresh fails (e.g. token expired), clear auth
                    console.error('Auth refresh failed:', err)
                    localStorage.removeItem('jt_user')
                    setUser(null)
                }
            }
        } catch (error) {
            console.error('Auth check failed:', error)
            localStorage.removeItem('jt_user')
            setUser(null)
        } finally {
            setLoading(false)
        }
    }

    async function login(username, password) {
        try {
            const response = await api.post('/api/auth/login', { username, password })
            if (response.success) {
                const userData = {
                    ...response.user,
                    token: response.token
                }
                setUser(userData)
                localStorage.setItem('jt_user', JSON.stringify(userData))
                return { success: true }
            } else {
                return { success: false, message: response.message || 'Login failed' }
            }
        } catch (error) {
            return { success: false, message: 'Network error. Please try again.' }
        }
    }


    async function logout() {
        try {
            await api.post('/api/auth/logout')
        } catch (error) {
            console.error('Logout error:', error)
        } finally {
            setUser(null)
            localStorage.removeItem('jt_user')
        }
    }

    /**
     * Page components (e.g. TimesheetEntry) call this on mount to register a
     * "save before idle logout" callback. Returns an unregister function for
     * use as a useEffect cleanup.
     */
    const registerIdleSaver = useCallback((fn) => {
        idleSaversRef.current.add(fn)
        return () => {
            idleSaversRef.current.delete(fn)
        }
    }, [])

    const handleIdleLogout = useCallback(async () => {
        // Only fires if a user is logged in
        if (!user) return

        // Try to save any in-progress drafts. Failures are logged but don't block logout.
        const savers = Array.from(idleSaversRef.current)
        for (const saver of savers) {
            try {
                await saver()
            } catch (err) {
                console.error('Idle save failed:', err)
            }
        }

        sessionStorage.setItem(
            'jt_auth_message',
            'You were logged out due to inactivity. Any work in progress has been saved as a draft.'
        )

        try {
            await logout()
        } catch (err) {
            console.error('Idle logout failed:', err)
        }

        // Hard navigate so React state is fully reset.
        window.location.href = '/login'
    }, [user])

    // Only run the idle timer when a user is logged in.
    useIdleTimeout(user ? IDLE_TIMEOUT_MS : null, handleIdleLogout)

    const value = {
        user,
        loading,
        login,
        logout,
        registerIdleSaver,
        isSysAdmin: user?.isSysAdmin,
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}